/**
 * DB 헬퍼 — queryOne / queryAll / insert / update / remove
 *
 * 모든 함수는 마지막 인자로 conn(커넥션)을 받을 수 있다.
 * conn을 넘기면 해당 커넥션을 사용하고, 생략하면 풀에서 자동으로 커넥션을 꺼낸다.
 * 트랜잭션 내에서 사용할 때는 반드시 conn을 넘겨야 한다.
 *
 * @example
 * // 단순 조회
 * const user = await queryOne<User>("SELECT * FROM users WHERE id = ?", [1]);
 *
 * // 트랜잭션 내 사용
 * await withTransaction(async (conn) => {
 *   const id = await insert("orders", { userId: 1, amount: 5000 }, conn);
 *   await update("users", { point: 0 }, { id: 1 }, conn);
 * });
 */

import { getPool, type DbConnection } from "./configure";

/**
 * WHERE 절 빌더
 * - null          → `col IS NULL`
 * - 배열          → `col IN (?, ?, ...)`
 * - 그 외(일반값) → `col = ?`
 */
function buildWhere(where: Record<string, any>): { sql: string; vals: any[] } {
    const clauses: string[] = [];
    const vals: any[] = [];

    for (const [k, v] of Object.entries(where)) {
        if (v === null) {
            clauses.push(`${k} IS NULL`);
        } else if (Array.isArray(v)) {
            if (v.length === 0) {
                throw new Error(`[db] where.${k}: 빈 배열은 사용할 수 없습니다.`);
            }
            clauses.push(`${k} IN (${v.map(() => "?").join(", ")})`);
            vals.push(...v);
        } else {
            clauses.push(`${k} = ?`);
            vals.push(v);
        }
    }

    return { sql: clauses.join(" AND "), vals };
}

/** 단일 row 조회. 결과가 없으면 null 반환. */
export const queryOne = async <T>(
    sql: string,
    params: any[] = [],
    conn?: DbConnection
): Promise<T | null> => {
    const [rows] = conn
        ? await conn.query(sql, params)
        : await getPool().query(sql, params);
    const data = rows as T[];
    return data.length > 0 ? data[0] : null;
};

/** 리스트 조회. 결과가 없으면 빈 배열 반환. */
export const queryAll = async <T>(
    sql: string,
    params: any[] = [],
    conn?: DbConnection
): Promise<T[]> => {
    const [rows] = conn
        ? await conn.query(sql, params)
        : await getPool().query(sql, params);
    return rows as T[];
};

/**
 * INSERT
 * @returns 삽입된 row의 insertId
 */
export const insert = async (
    table: string,
    row: Record<string, any>,
    conn?: DbConnection
): Promise<number> => {
    if (!table || !row || Object.keys(row).length === 0) {
        throw new Error("[db] insert: table과 row가 필요합니다.");
    }
    const keys = Object.keys(row);
    const vals = keys.map((k) => row[k]);
    const sql = `INSERT INTO ${table} (${keys.join(", ")}) VALUES (${keys.map(() => "?").join(", ")})`;
    const [result] = conn
        ? await conn.query(sql, vals)
        : await getPool().query(sql, vals);
    if (!result || (result as any).insertId === 0) {
        throw new Error("[db] insert: 데이터 삽입에 실패했습니다.");
    }
    return (result as any).insertId;
};

/**
 * UPDATE
 * where 조건: null(IS NULL), 배열(IN), 일반값(=) 모두 지원.
 * @returns affectedRows
 */
export const update = async (
    table: string,
    row: Record<string, any>,
    where: Record<string, any>,
    conn?: DbConnection
): Promise<number> => {
    if (
        !table ||
        !row || Object.keys(row).length === 0 ||
        !where || Object.keys(where).length === 0
    ) {
        throw new Error("[db] update: table, row, where가 필요합니다.");
    }
    const keys = Object.keys(row);
    const vals = keys.map((k) => row[k]);
    const setSql = keys.map((k) => `${k} = ?`).join(", ");
    const { sql: whereSql, vals: whereVals } = buildWhere(where);
    const sql = `UPDATE ${table} SET ${setSql} WHERE ${whereSql}`;
    const [result] = conn
        ? await conn.query(sql, [...vals, ...whereVals])
        : await getPool().query(sql, [...vals, ...whereVals]);
    if (!result || (result as any).affectedRows === 0) {
        throw new Error("[db] update: 데이터 수정에 실패했습니다.");
    }
    return (result as any).affectedRows;
};

/**
 * DELETE
 * where 조건: null(IS NULL), 배열(IN), 일반값(=) 모두 지원.
 * @returns affectedRows
 */
export const remove = async (
    table: string,
    where: Record<string, any>,
    conn?: DbConnection
): Promise<number> => {
    if (!table || !where || Object.keys(where).length === 0) {
        throw new Error("[db] remove: table과 where가 필요합니다.");
    }
    const { sql: whereSql, vals: whereVals } = buildWhere(where);
    const sql = `DELETE FROM ${table} WHERE ${whereSql}`;
    const [result] = conn
        ? await conn.query(sql, whereVals)
        : await getPool().query(sql, whereVals);
    if (!result || (result as any).affectedRows === 0) {
        throw new Error("[db] remove: 데이터 삭제에 실패했습니다.");
    }
    return (result as any).affectedRows;
};
